// @ts-expect-error
import html2pdf from 'html2pdf.js';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  beginHtml2PdfExport,
  endHtml2PdfExport,
} from '@/utils/html2pdf-overlay';
import { AccoutingRetrocessionFeeViewModel } from '../use-retrocession-fee.viewmodel';

export const useExportAccountingRetrocessionFeeToPdfViewModel = ({
  information,
}: {
  information: AccoutingRetrocessionFeeViewModel['information'];
}) => {
  const contentRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);

  // Fonction pour exporter en PDF
  const exportToPdf = async (): Promise<void> => {
    if (!information) {
      return;
    }

    setExporting(true);

    const filename = `${t('accounting-retrocession-fee.export-filename', {
      doctorAttending: information.doctorAttending.lastname.toLowerCase(),
      doctorSubstitute: information.doctorSubstitute.lastname.toLowerCase(),
      from: String(information.period.from),
      to: String(information.period.to),
    })}.pdf`;

    // Configuration de html2pdf
    const options = {
      margin: 10,
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 4, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    };

    beginHtml2PdfExport();
    try {
      await html2pdf().from(contentRef.current).set(options).save();
    } finally {
      endHtml2PdfExport();
      setExporting(false);
    }
  };

  // Generate the PDF as a Blob without triggering a browser download. Used by
  // the ZIP export to bundle many retrocession notes into a single archive.
  const exportToBlob = async (): Promise<Blob | null> => {
    if (!information || !contentRef.current) {
      return null;
    }

    const filename = `${t('accounting-retrocession-fee.export-filename', {
      doctorAttending: information.doctorAttending.lastname.toLowerCase(),
      doctorSubstitute: information.doctorSubstitute.lastname.toLowerCase(),
      from: String(information.period.from),
      to: String(information.period.to),
    })}.pdf`;

    const options = {
      margin: 10,
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 4, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    };

    setExporting(true);
    beginHtml2PdfExport();
    try {
      const blob: Blob = await html2pdf()
        .from(contentRef.current)
        .set(options)
        .outputPdf('blob');
      return blob;
    } catch {
      return null;
    } finally {
      endHtml2PdfExport();
      setExporting(false);
    }
  };

  return {
    contentRef,
    exporting,
    export: exportToPdf,
    exportToBlob,
  };
};
export type ExportAccountingRetrocessionFeeToPdfViewModel = ReturnType<
  typeof useExportAccountingRetrocessionFeeToPdfViewModel
>;
